import PropTypes from 'prop-types';
import { always } from 'ramda';

import { v4 as uuid } from 'uuid';
import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { CircularProgress, Container, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { FormattedMessage } from 'react-intl';
import { FullLogo, MiniProfile } from '@classity-edtech/ui-base/dist/components/Displays';
import { useLazySignedUrlQuery } from '../../services/signedUrl';
import { useAppDispatch } from '../../store/hooks';
import { updateFlashMessage } from '../../store/actions/flashMessage';
import { Button, RadioButton, TextField } from '@classity-edtech/ui-base/dist/components/Inputs';
import { useNavigate } from 'react-router-dom';
import { useUpdateProfileMutation } from '../../services/profile';
import { formatError, generateImageAssestUrl, windowExists } from '@classity-edtech/ui-base';
import { MediaType } from '@classity-edtech/platform-utils';
import { webPaths } from '../../routes/paths';
import { useGetMyProfileQuery } from '../../services/users';

type DisplayName = 'profileName' | 'username';

export interface ProfileProps extends MuiBoxProps {}

export const Profile = ({ className }: ProfileProps) => {
  const dispatch: any = useAppDispatch();
  const [signedUrl] = useLazySignedUrlQuery();
  const screens = useAppMediaQuery();
  const maxBioLength = 250;
  const navigate = useNavigate();

  const { data: user } = useGetMyProfileQuery();

  const [uploading, setUploading] = useState(false);
  const [avatar, setAvatar] = useState('');
  const [displayName, setDisplayName] = useState<DisplayName>('profileName');
  const [profile, setProfile] = useState({
    firstname: '',
    lastname: '',
    bio: '',
    username: '',
    avatar: undefined,
  });

  const [updateProfile, updateProfileResult] = useUpdateProfileMutation();

  useEffect(() => {
    if (!user?.profile) return;

    setProfile(values => ({
      firstname: user.profile?.firstname ?? values.firstname,
      lastname: user.profile?.lastname ?? values.lastname,
      bio: user.profile?.bio ?? values.bio,
      username: user.profile?.username ?? values.username,
      avatar: user.profile?.avatar?.contentUrl
        ? {
            contentUrl: user.profile.avatar.contentUrl,
            medium: MediaType.IMAGE,
          }
        : values.avatar,
    }));

    setDisplayName(user.profile?.username ? 'username' : 'profileName');
  }, [user]);

  const incompleteProfile = !profile.firstname || !profile.lastname || !profile.username;

  const navigateHome = () => {
    navigate('/');
  };

  const getBase64 = (file): Promise<string | ArrayBuffer | null> =>
    new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = function () {
        resolve(reader.result);
      };
      reader.onerror = function (error) {
        reject(error);
      };
    });

  const dataURLtoBlob = dataurl => {
    let arr = dataurl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = (windowExists as any).atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      const target = e.target as HTMLInputElement;
      const image = target?.files?.[0];

      if (!image) {
        return;
      }

      const extension = image.type?.split('/')[1] || image.name.split('.').pop() || 'png';
      const path = `${new Date()
        .toISOString()
        .split('T')[0]
        .replace(/-/g, '/')}/${uuid().replace(/-/g, '')}`;

      const signedUrlResult = await signedUrl({
        mimeType: image.type,
        path,
      });

      if ('error' in signedUrlResult) {
        return dispatch(
          updateFlashMessage({
            open: true,
            severity: 'error',
            message: 'Unable to upload image',
          }),
        );
      }

      const imageBase64 = await getBase64(image);
      const response = await fetch(signedUrlResult.data.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': image.type,
        },
        body: dataURLtoBlob(imageBase64),
      });

      if (!response.ok) {
        dispatch(
          updateFlashMessage({
            open: true,
            severity: 'error',
            message: 'Image upload failed',
          }),
        );
      }

      setAvatar(imageBase64 as string);
      handleSetAvatar(`${path}.${extension}`);
    } catch (error) {
      dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'Image upload fails',
        }),
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSetAvatar = (url: string) => {
    setProfile(values => ({
      ...values,
      avatar: { contentUrl: url, medium: MediaType.IMAGE },
    }));
  };

  const handleInputChange = (e: React.FormEvent<EventTarget>) => {
    const target = e.target as HTMLInputElement;

    if (target.name === 'bio' && target.value.length > maxBioLength) return;

    setProfile(values => ({
      ...values,
      [target.name]: target.value,
    }));
  };

  const handleDisplayName = (e: React.FormEvent<EventTarget>) => {
    const target = e.target as HTMLInputElement;

    setDisplayName(target.value as any);
  };

  const handleProfileUpdate = async () => {
    if (incompleteProfile) {
      return dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'Incomplete profile details!',
        }),
      );
    }

    const updateProfileResultData = await updateProfile({
      userId: user?.id,
      profileInput: profile,
    });

    if ('error' in updateProfileResultData) {
      const error = formatError(updateProfileResultData.error.message);
      return dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'An error occurred!',
        }),
      );
    }

    if ('data' in updateProfileResultData) {
      navigate(`/${webPaths.studentCountry}`);
    }
  };

  const localisedMessage = (id: string) => {
    const F: any = FormattedMessage;
    return <F id={id} />;
  };

  return (
    <Container>
      <Box
        sx={{
          py: { xs: 1, md: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <FullLogo
          sx={{ width: { xs: '82px', md: '140px' }, height: 'auto' }}
          onClick={navigateHome}
        />
        <Button
          sx={{
            py: 1.5,
            px: 3,
            height: screens.isMobile ? '64px' : '48px',
            zIndex: 20,
          }}
          isLoading={updateProfileResult.isLoading}
          onClick={handleProfileUpdate}
          showRightIcon={screens.isMobile ? true : false}
        >
          <Typography variant='body3SemiBold' sx={{ textTransform: 'uppercase' }}>
            Continue
          </Typography>
        </Button>
      </Box>

      <Box sx={{ width: { xs: '100%', md: '568px' }, my: { xs: 3, md: 6 }, mx: 'auto' }}>
        <Typography
          variant={screens.isMobile ? 'subtitle5' : 'subtitle3'}
          sx={{ textAlign: { md: 'center' }, display: 'block', mx: 'auto' }}
        >
          Create your profile
        </Typography>
        <Typography
          variant={screens.isMobile ? 'body3' : 'body2'}
          sx={{
            maxWidth: '500px',
            textAlign: { md: 'center' },
            display: 'block',
            mb: 4,
            mx: 'auto',
          }}
        >
          Others will see a summary of your profile on Classity Community like this. You'll always
          be able to modify this later in your preferences.
        </Typography>
        <Box
          sx={{
            boxShadow: '0px 0px 40px 0px rgba(69, 71, 69, 0.20)',
            maxWidth: { xs: '100%', md: '400px' },
            mb: 4,
            mx: 'auto',
            borderRadius: '8px',
          }}
        >
          <MiniProfile
            bio={profile.bio}
            name={
              displayName === 'profileName'
                ? `${profile.firstname} ${profile.lastname}`
                : profile.username
            }
            src={avatar || (profile.avatar ? generateImageAssestUrl(profile.avatar, 2) : '')}
            badge='Member'
            handleUploadImage={handleUploadImage}
            showUpload={!avatar}
          />
        </Box>

        <Box className={className} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ mt: -1, ml: -1 }}>
              <RadioButton
                onChange={handleDisplayName}
                checked={displayName === 'profileName'}
                value={'profileName'}
                name='displayName'
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
              <TextField
                type={'text'}
                label={'First name'}
                name={'firstname'}
                value={profile.firstname}
                onChange={handleInputChange}
              />
              <TextField
                type={'text'}
                label={'Last name'}
                name={'lastname'}
                value={profile.lastname}
                onChange={handleInputChange}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ mt: -1, ml: -1 }}>
              <RadioButton
                onChange={handleDisplayName}
                checked={displayName === 'username'}
                value={'username'}
                name='displayName'
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
              <TextField
                type={'text'}
                label={'Username'}
                name={'username'}
                value={profile.username}
                onChange={handleInputChange}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <TextField
              type={'textarea'}
              sx={{ '& .MuiInputBase-input': { height: '48px' } }}
              label={'Short Bio (optional)'}
              name={'bio'}
              value={profile.bio}
              onChange={handleInputChange}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Typography variant='body3' sx={{ display: { xs: 'none', md: 'block' } }}>
                A one line statement about you
              </Typography>
              <Typography variant='body3'>
                {profile.bio.length}/{maxBioLength}
              </Typography>
            </Box>
          </Box>
        </Box>
        {uploading && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.2)',
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Container>
  );
};

Profile.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  nextScreen: PropTypes.func,
};

Profile.defaultProps = {
  children: null,
  className: null,
  nextScreen: always,
};

export default styled<any>(Profile)(
  ({ theme }) => `
  `,
);
